import { type ReactNode, useEffect, useRef, useState } from "react";
import { usePostHog } from "../../hooks/usePostHog";
import { recordInstallationChoice } from "../../utils/installationChoice";
import { copyToClipboard } from "../../utils/ui";
import "./remoteSetup.css";
import SetupHelp from "./SetupHelp";
import { listen } from "./setupChannel";
import { asClientName, detectOs, OS_COPY, type OsKey, rememberClient, WINDOWS_CMD_COPY } from "./setupCopy";

type Card = "copy" | "run" | "verify";
type SetupMethod = "automatic" | "npx" | "cmd" | "download";

interface RemoteSetupFlowProps {
    /** Assistant that started the OAuth run, from the server. Null when unrecognised. */
    client?: string | null;
    /**
     * Which journey this is, for analytics. "oauth_setup" and "devices_page" are the values the events
     * used before this flow existed; "device_reconnect" is the Devices page Reconnect dialog.
     */
    source?: "oauth_setup" | "devices_page" | "device_reconnect";
    /** Shown with a sign-out link above the command. Omitted where the page already shows the account. */
    userEmail?: string;
    userId?: string;
    onSignOut?: () => void;
    /**
     * How many devices this user has right now. The flow remembers the count it started with and
     * treats an increase as "the command ran and registered", which is the one rule that works both
     * for a first device and for adding another one from a dashboard that already lists some.
     */
    deviceCount: number;
    /**
     * Set when bringing back a known offline device. The copy names that computer, and Verify only
     * shows if it has to sign in again: otherwise it restores its saved session by itself.
     */
    reconnectDeviceName?: string;
    /**
     * Extra content under the first card only, such as a quiet pointer to an alternative. Later
     * cards keep the setup help as their only footer, so nothing distracts once someone has started.
     */
    copyCardFooter?: ReactNode;
}

const CARDS: {
    key: Card;
    label: string;
}[] = [
    {
        key: "copy",
        label: "Choose method",
    },
    {
        key: "run",
        label: "Start on device",
    },
    {
        key: "verify",
        label: "Verify",
    },
];

/**
 * The setup flow: three cards, one action each. Markup and styles are a direct port of the approved
 * prototype (design/remote-onboarding/index.html, v5); the reasoning for every decision here is in
 * design/remote-onboarding/notes.md.
 *
 * This is the only place the connect instructions exist. It is used by the OAuth screen on
 * /add-device, by the Devices dashboard when the list is empty, by its "Add a device" dialog and by
 * its Reconnect dialog, so the wording, the OS switch, the help list and the events cannot drift
 * between those journeys.
 */
const RemoteSetupFlow = ({
    client,
    source = "oauth_setup",
    userEmail,
    userId,
    onSignOut,
    deviceCount,
    reconnectDeviceName,
    copyCardFooter,
}: RemoteSetupFlowProps) => {
    const [card, setCard] = useState<Card>("copy");
    const [os, setOs] = useState<OsKey>(() => detectOs());
    const [method, setMethod] = useState<SetupMethod>("automatic");
    const [windowsChoice, setWindowsChoice] = useState<"download" | "automatic" | "npx">("download");
    const [copying, setCopying] = useState(false);
    const { track, trackOnce } = usePostHog();
    const trackedView = useRef(false);
    // Why the flow moved to the card it is on. Set immediately before each setCard, read by the
    // effect below: most moves here are automatic, so without this every step looks the same.
    const moveReason = useRef<"start" | "command_copied" | "verify_tab_opened" | "device_registered" | "back">("start");
    const lastTrackedCard = useRef<string>("");

    const copy = method === "cmd" && os === "windows" ? WINDOWS_CMD_COPY : OS_COPY[os];
    const downloadUrl = "/remote-windows.cmd";
    const clientName = asClientName(client);
    // "unknown" rather than null so a PostHog breakdown has a real bucket to show instead of an
    // empty one, and so "we could not tell" is distinguishable from "the property was not sent".
    const clientProp = clientName ?? "unknown";
    const cards = reconnectDeviceName && card !== "verify" ? CARDS.filter((c) => c.key !== "verify") : CARDS;
    const activeIndex = cards.findIndex((c) => c.key === card);

    useEffect(() => {
        if (!userId || trackedView.current) {
            return;
        }
        trackedView.current = true;
        void trackOnce("device_install_prompt_viewed", `${source}.${userId}`, {
            user_id: userId,
            source,
            client: clientProp,
        });
    }, [
        trackOnce,
        userId,
        clientProp,
        source,
    ]);

    // Every card the user reaches, however they got there. The two automatic moves (the verify tab
    // announcing itself, and a device row appearing) are the steps we most need to see, and neither
    // goes through a click, so this cannot hang off a handler.
    useEffect(() => {
        if (!userId || lastTrackedCard.current === card) {
            return;
        }
        lastTrackedCard.current = card;
        void trackOnce("onboarding_card_viewed", `${source}.${card}.${userId}`, {
            user_id: userId,
            card,
            reason: moveReason.current,
            client: clientProp,
            detected_os: OS_COPY[os].short,
            source,
        });
    }, [
        card,
        os,
        trackOnce,
        userId,
        clientProp,
        source,
    ]);

    // Hand the assistant to the verify tab, which the CLI opens without any query params.
    //
    // Only the OAuth journey writes this. localStorage is shared across the whole origin, not per
    // tab, so a dashboard open in another tab — which never has a client — would otherwise clear
    // what a live OAuth run just wrote, and the verify tab would lose the "Try this prompt in
    // ChatGPT" link it exists to show.
    useEffect(() => {
        if (source === "oauth_setup") {
            rememberClient(clientName);
        }
    }, [
        clientName,
        source,
    ]);

    // The verify tab tells us it has opened (same origin), which is the moment the command has
    // actually run. Without it this card would only appear after verification, far too late.
    useEffect(
        () =>
            listen("verify_opened", () => {
                moveReason.current = "verify_tab_opened";
                setCard("verify");
            }),
        [],
    );

    // A new device row is the later, database-backed signal for the same thing: it means the device
    // registered, so verification is either pending or just happened. Compared against the count
    // this flow opened with, so the dashboard's "add another device" does not start on card 3
    // because devices already exist.
    const startingDeviceCount = useRef<number | null>(null);
    useEffect(() => {
        if (startingDeviceCount.current === null) {
            startingDeviceCount.current = deviceCount;
            return;
        }
        if (deviceCount > startingDeviceCount.current) {
            moveReason.current = "device_registered";
            setCard("verify");
        }
    }, [
        deviceCount,
    ]);

    const handleCopy = async (nextMethod: SetupMethod) => {
        const selectedCopy = nextMethod === "cmd" ? WINDOWS_CMD_COPY : OS_COPY[os];
        const command = nextMethod === "npx" ? selectedCopy.command : selectedCopy.setupCommand;
        setCopying(true);
        try {
            const copied = await copyToClipboard(command, "Now paste it in " + selectedCopy.term + " and press Enter");
            if (!copied) {
                return;
            }
            setMethod(nextMethod);
            recordInstallationChoice(userId, os, nextMethod === "automatic" ? "script" : nextMethod === "cmd" ? "script_cmd" : "npx");
            void track("device_install_command_copied", {
                user_id: userId,
                source,
                client: clientProp,
                os,
                installation_method: nextMethod === "automatic" ? "script" : nextMethod === "cmd" ? "script_cmd" : "npx",
            });
            moveReason.current = "command_copied";
            setCard("run");
        } finally {
            setCopying(false);
        }
    };

    const goTo = (next: Card) => {
        moveReason.current = "back";
        setCard(next);
    };

    const osSwitch = (
        <div className="rs-os-options" role="group" aria-label="Device operating system">
            {(Object.keys(OS_COPY) as OsKey[]).map((key) => (
                <button
                    key={key}
                    type="button"
                    aria-pressed={os === key}
                    disabled={copying}
                    onClick={() => {
                        if (os === key) {
                            return;
                        }
                        void track("onboarding_os_switched", {
                            user_id: userId,
                            from: os,
                            to: key,
                            client: clientProp,
                            card,
                            source,
                        });
                        setOs(key);
                        setMethod("automatic");
                        // A different OS needs a fresh copy; the clipboard still holds the previous command.
                        goTo("copy");
                    }}
                >
                    {key === "mac" ? "macOS" : OS_COPY[key].short}
                </button>
            ))}
        </div>
    );

    return (
        <div className="rs-root">
            <div className="rs-progress">
                {cards.map((c, i) => (
                    <div key={c.key} className={`rs-step${i < activeIndex ? " is-done" : ""}${i === activeIndex ? " is-active" : ""}`}>
                        {c.label}
                    </div>
                ))}
            </div>

            {card === "copy" && (
                <section>
                    {reconnectDeviceName ? (
                        <>
                            <h1 className="rs-h1">Reconnect {reconnectDeviceName}</h1>
                            <p className="rs-lede">
                                Run the command on {reconnectDeviceName} to bring it back online. This window closes when it's connected. Choose how
                                to connect.
                            </p>
                        </>
                    ) : (
                        <>
                            <h1 className="rs-h1">Connect this device</h1>
                            <p className="rs-lede">
                                Desktop Commander Remote runs on this device so {clientName ?? "your AI chat"} can work with your files. Choose how to
                                connect.
                            </p>
                        </>
                    )}
                    {/* Only where the account is not already on screen: the OAuth screen is a bare
                        page, the dashboard has the signed-in account in its sidebar. */}
                    {userEmail && onSignOut && (
                        <p className="rs-account">
                            {userEmail} ·{" "}
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    onSignOut();
                                }}
                            >
                                Sign out
                            </a>
                        </p>
                    )}
                    {osSwitch}
                    {os === "windows" && (
                        <div className="rs-windows-choices" data-testid="windows-install-choices">
                            <p className="rs-choice-intro">Choose one method. You can change it anytime.</p>
                            {(
                                [
                                    "download",
                                    "automatic",
                                    "npx",
                                ] as const
                            ).map((choice) => {
                                const selected = windowsChoice === choice;
                                const title =
                                    choice === "download"
                                        ? "Download and run"
                                        : choice === "automatic"
                                          ? "PowerShell setup script"
                                          : "npx (advanced)";
                                const description =
                                    choice === "download"
                                        ? "Easiest option. No commands to type."
                                        : choice === "automatic"
                                          ? "One command. Installs Node.js if needed."
                                          : "Requires Node.js 22.12+ already installed.";
                                return (
                                    <div
                                        key={choice}
                                        className={`rs-method-card ${selected ? "is-selected" : ""}`}
                                        data-testid={`windows-method-${choice}`}
                                    >
                                        <button
                                            type="button"
                                            className="rs-method-selector"
                                            aria-expanded={selected}
                                            aria-controls={`rs-windows-panel-${choice}`}
                                            onClick={() => setWindowsChoice(choice)}
                                        >
                                            <span className="rs-method-text">
                                                <span className="rs-method-heading">
                                                    {title}
                                                    {choice === "download" && <span className="rs-choice-recommended">Recommended</span>}
                                                </span>
                                                <span className="rs-method-description">{description}</span>
                                            </span>
                                            <span className="rs-method-chevron" aria-hidden="true">
                                                {selected ? "⌃" : "⌄"}
                                            </span>
                                        </button>
                                        <div id={`rs-windows-panel-${choice}`} hidden={!selected} className="rs-method-panel">
                                            {choice === "download" ? (
                                                <div data-testid="windows-launcher-download">
                                                    <a
                                                        className="rs-primary"
                                                        href={downloadUrl}
                                                        download="DesktopCommander-Remote.cmd"
                                                        onClick={() => {
                                                            setMethod("download");
                                                            recordInstallationChoice(userId, "windows", "download");
                                                            void track("device_install_download_clicked", {
                                                                user_id: userId,
                                                                source,
                                                                client: clientProp,
                                                                os: "windows",
                                                                installation_method: "download",
                                                            });
                                                        }}
                                                        data-testid="download-windows-launcher"
                                                    >
                                                        Download for Windows (.cmd)
                                                    </a>
                                                    <p>
                                                        Double-click the downloaded file to connect. Keep its window open while using Desktop
                                                        Commander.
                                                    </p>
                                                </div>
                                            ) : (
                                                <>
                                                    {choice === "npx" && (
                                                        <p data-testid="windows-node-prerequisite">
                                                            Don't have Node.js? <a href={copy.nodeUrl}>Install Node.js for Windows</a> first.
                                                        </p>
                                                    )}
                                                    <div
                                                        className="rs-choice-command"
                                                        data-testid={choice === "automatic" ? "setup-command" : "npx-command"}
                                                    >
                                                        <code>{choice === "automatic" ? OS_COPY.windows.setupCommand : OS_COPY.windows.command}</code>
                                                        <button
                                                            type="button"
                                                            disabled={copying}
                                                            onClick={() => handleCopy(choice)}
                                                            data-testid={choice === "automatic" ? "copy-command" : "copy-npx-command"}
                                                        >
                                                            Copy
                                                        </button>
                                                    </div>
                                                    <p>After copying, we'll show you how to open PowerShell and run the command.</p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {os !== "windows" && (
                        <>
                            <div className="rs-setup-heading">
                                <strong>{os === "windows" ? "Run setup script" : "Quick setup"}</strong>
                                {os !== "windows" && <span>Recommended</span>}
                            </div>
                            <p className="rs-setup-note">
                                Starts Desktop Commander. If Node.js can’t be found, you’ll be asked before installing it.
                            </p>
                            <div className="rs-command" data-testid="setup-command">
                                <span className="rs-dollar" aria-hidden="true">
                                    {OS_COPY[os].promptChar}
                                </span>
                                <code>{OS_COPY[os].setupCommand}</code>
                            </div>
                            <button
                                type="button"
                                className="rs-primary"
                                disabled={copying}
                                onClick={() => handleCopy("automatic")}
                                data-testid="copy-command"
                            >
                                {os === "windows" ? "Copy PowerShell command" : "Copy setup command"}
                            </button>
                            <p className="rs-script-source">
                                All these scripts are open source. You can{" "}
                                <a href={OS_COPY[os].setupSourceUrl} target="_blank" rel="noopener noreferrer">
                                    view them on GitHub ↗
                                </a>
                            </p>

                            <details className="rs-alternatives" open={method === "npx" || undefined}>
                                <summary>
                                    <span className="rs-alternatives-label">
                                        <strong>Other ways to connect</strong>
                                        <span>Use your own Node.js installation</span>
                                    </span>
                                    <svg
                                        className="rs-alternatives-chevron"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        aria-hidden="true"
                                    >
                                        <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </summary>
                                <div className="rs-alternative">
                                    <strong>Use your own Node.js installation</strong>
                                    <p>Requires Node.js 22.12 or newer.</p>
                                    <div className="rs-command" data-testid="npx-command">
                                        <code>{copy.command}</code>
                                    </div>
                                    <button type="button" className="rs-secondary" disabled={copying} onClick={() => handleCopy("npx")}>
                                        Copy npx command
                                    </button>
                                    {os === "windows" && (
                                        <p data-testid="windows-node-prerequisite">
                                            <a href={copy.nodeUrl}>Install Node.js for Windows</a>, then reopen PowerShell. This command uses{" "}
                                            <code>npx.cmd</code>, so you don’t need to change execution policies.
                                        </p>
                                    )}
                                </div>
                            </details>
                        </>
                    )}
                </section>
            )}

            {card === "run" && (
                <section>
                    <h1 className="rs-h1">{copy.title}</h1>
                    {osSwitch}

                    <div className="rs-steprow">
                        <i>1</i>
                        <div>
                            {copy.open.before}
                            <code className="rs-literal">"{copy.open.literal}"</code>
                            {copy.open.after}
                        </div>
                    </div>
                    <div className="rs-steprow">
                        <i>2</i>
                        <div>{copy.paste}</div>
                    </div>

                    <span className="rs-outcome">
                        {reconnectDeviceName
                            ? "It comes back online in a few seconds. If it asks you to sign in, a page opens in your browser."
                            : "A verification page then opens in your browser by itself."}
                    </span>
                    <p className="rs-setup-note rs-keep-open">
                        Keep {copy.term} open while connected. Press Ctrl+C to stop; run the same command again to reconnect.
                    </p>

                    <button type="button" className="rs-secondary" onClick={() => goTo("copy")}>
                        ← Back to the command
                    </button>
                </section>
            )}

            {card === "verify" && (
                <section>
                    <h1 className="rs-h1">Verify this device</h1>
                    <p className="rs-lede">
                        A new tab opens by itself. Click <strong>Verify device</strong> there, and keep {copy.term} running.
                    </p>
                    <div className="rs-waiting">
                        <span className="rs-pulse" aria-hidden="true" />
                        <div>
                            <strong>Waiting for you to verify…</strong>
                            <span>This page finishes by itself once you do.</span>
                        </div>
                    </div>
                    <button type="button" className="rs-secondary" onClick={() => goTo("run")}>
                        ← Back to the previous step
                    </button>
                </section>
            )}

            {card === "copy" && copyCardFooter}

            {/* Same escape hatch on the two cards where someone can get stuck. */}
            {card !== "copy" && <SetupHelp os={copy} automatic={method !== "npx"} client={clientName} card={card} source={source} userId={userId} />}
        </div>
    );
};

export default RemoteSetupFlow;
